import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Logo from "../components/Logo";
import { Barbell, Calendar, Target, Warning } from "@phosphor-icons/react";
import { exportRoutinePDF } from "../lib/pdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicRoutine() {
  const { token } = useParams();
  const [routine, setRoutine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/public/routines/${token}`)
      .then((r) => setRoutine(r.data))
      .catch((err) => setError(err.response?.data?.detail || "Error al cargar la rutina"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <Logo size={120} className="animate-pulse" />
      </div>
    );
  }

  if (error || !routine) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center p-4">
        <div className="card-base p-10 text-center max-w-md">
          <Warning size={56} weight="duotone" className="text-[#FF3B30] mx-auto mb-4" />
          <h1 className="font-display uppercase font-black text-3xl mb-2">Link no disponible</h1>
          <p className="text-muted text-sm mb-6">{error || "Esta rutina fue revocada o el link es incorrecto."}</p>
          <Link to="/login" className="btn-primary inline-block">Ir a Athlon Sports</Link>
        </div>
      </div>
    );
  }

  const totalExercises = routine.exercises?.length || 0;
  const totalVolume = (routine.exercises || []).reduce((acc, ex) => acc + (ex.sets || 0) * (ex.reps || 0) * (ex.weight_kg || 0), 0);

  return (
    <div className="min-h-screen bg-app noise-bg" data-testid="public-routine-page">
      {/* Top bar */}
      <header className="border-b border-app bg-[#0A0A0A]/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-3" data-testid="public-header-logo">
            <Logo size={40} variant="red" />
            <div>
              <div className="font-display font-black uppercase tracking-tighter text-base leading-none">Athlon Sports</div>
              <div className="text-[10px] uppercase tracking-widest text-muted">Training Control</div>
            </div>
          </Link>
          <span className="text-[10px] uppercase tracking-widest text-muted hidden sm:block">Rutina compartida</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-10">
        {/* Hero */}
        <div className="card-base p-8 mb-6" data-testid="public-routine-hero">
          <div className="text-xs uppercase tracking-widest text-primary-app mb-2">
            {routine.date ? new Date(routine.date + "T00:00:00").toLocaleDateString("es-AR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }) : "Sin fecha"}
          </div>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tighter mb-4" data-testid="public-routine-name">{routine.name}</h1>

          {routine.athlete_name && (
            <div className="text-sm text-muted mb-4">
              Para <span className="text-white font-semibold">{routine.athlete_name}</span>
              {routine.athlete_sport && <span> · {routine.athlete_sport}</span>}
            </div>
          )}

          {routine.objectives && (
            <div className="flex items-start gap-3 border-l-2 border-[#FF3B30] pl-4 mb-5">
              <Target size={18} weight="duotone" className="text-primary-app mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted">Objetivos del día</div>
                <div className="text-sm text-white">{routine.objectives}</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 pt-5 border-t border-app">
            <Stat icon={<Barbell size={18} weight="duotone" />} label="Ejercicios" value={totalExercises} />
            <Stat icon={<Calendar size={18} weight="duotone" />} label="Series totales" value={(routine.exercises || []).reduce((a, e) => a + (e.sets || 0), 0)} />
            <Stat icon={<Target size={18} weight="duotone" />} label="Volumen (kg)" value={totalVolume.toFixed(0)} />
          </div>

          <div className="mt-6 flex gap-3 flex-wrap">
            <button
              onClick={() => exportRoutinePDF(routine, { name: routine.athlete_name })}
              className="btn-secondary"
              data-testid="public-download-pdf"
            >
              Descargar PDF
            </button>
          </div>
        </div>

        {/* Exercises */}
        {totalExercises === 0 ? (
          <div className="card-base p-10 text-center text-muted">Esta rutina no tiene ejercicios cargados.</div>
        ) : (
          <div className="space-y-3" data-testid="public-exercises-list">
            {routine.exercises.map((ex, i) => (
              <div key={i} className="card-base p-4 flex gap-4" data-testid={`public-ex-${i}`}>
                {ex.image_url && (
                  <img src={ex.image_url} alt="" className="w-24 h-24 object-cover flex-shrink-0 hidden sm:block" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest bg-[#FF3B30] text-white px-2 py-0.5 font-bold">
                      #{i + 1}
                    </span>
                    <div className="font-display font-bold uppercase text-lg">{ex.exercise_name}</div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <Metric label="Peso" value={`${ex.weight_kg || 0} kg`} />
                    <Metric label="Reps" value={ex.reps || 0} />
                    <Metric label="Series" value={ex.sets || 0} />
                    <Metric label="μ Pausa" value={`${ex.micro_pause_sec || 0}s`} />
                    <Metric label="M Pausa" value={`${ex.macro_pause_sec || 0}s`} />
                  </div>
                  {ex.notes && <div className="text-xs text-muted mt-3 italic">Nota: {ex.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer CTA */}
        <div className="card-base p-6 mt-8 text-center">
          <div className="text-xs uppercase tracking-widest text-muted mb-3">Potenciado por</div>
          <Link to="/login" className="font-display font-black uppercase text-2xl tracking-tighter hover:text-primary-app transition-colors">
            Athlon Sports — Training Control
          </Link>
          <div className="text-xs text-muted mt-2">Gestión profesional de entrenamientos físicos</div>
        </div>
      </main>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-primary-app mt-0.5">{icon}</span>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted">{label}</div>
        <div className="font-display font-black text-xl">{value}</div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted mb-0.5">{label}</div>
      <div className="text-sm font-semibold text-white">{value}</div>
    </div>
  );
}
